import { useEffect, useMemo } from 'react';
import firebase from 'firebase/app';
import 'firebase/firestore';
import { Collections } from '../../firebase/firestore/collections';
import { useUserIsAuthenticated } from '../../app/hooks/useUserIsAuthenticated';

interface WatcherOptions {
  candidateIds: string[];
  onChange: (changed: boolean) => void;
}

const chunk = <T>(arr: T[], size: number): T[][] =>
  Array.from({ length: Math.ceil(arr.length / size) }, (_, i) => arr.slice(i * size, i * size + size));

export function useCandidateOnDemandInterviewWatcher({ candidateIds, onChange }: WatcherOptions): void {
  const userIsAuthenticated = useUserIsAuthenticated();

  const sortedCandidateIds = useMemo(() => {
    return [...candidateIds].sort();
  }, [candidateIds]);

  useEffect(() => {
    const previousAppointments = new Map();

    if (!userIsAuthenticated || sortedCandidateIds.length === 0) {
      return;
    }

    const candidateIdChunks = chunk(sortedCandidateIds, 10);

    const unsubscribes = candidateIdChunks.map((idChunk) => {
      return firebase
        .firestore()
        .collection(Collections.CANDIDATES)
        .where(firebase.firestore.FieldPath.documentId(), 'in', idChunk)
        .onSnapshot((snapshot) => {
          let hasChanged = false;
          // eslint-disable-next-line no-restricted-syntax
          for (const change of snapshot.docChanges()) {
            const docId = change.doc.id;
            // eslint-disable-next-line
            const newAppointment = change.doc.data()?.appointment;
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const prevAppointment = previousAppointments.get(docId);

            if (change.type === 'modified') {
              // istanbul ignore next
              // eslint-disable-next-line
              if (newAppointment.status !== prevAppointment?.status) {
                hasChanged = true;
              }
            }

            // Update cached value regardless of change type
            previousAppointments.set(docId, newAppointment);
          }

          if (hasChanged) {
            onChange(true);
          }
        });
    });
    // eslint-disable-next-line
    return () => {
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    };
  }, [userIsAuthenticated, sortedCandidateIds, onChange]);
}
